import { GenericField } from "@/lib/Components/Form/Fields/GenericField";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  LexicalEditor,
  TextNode,
  $createTextNode,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  COMMAND_PRIORITY_HIGH,
} from "lexical";
import { useState, useRef, useEffect } from "react";
import { LexicalOnFocusPlugin } from "@/lib/Components/Lexical/plugins/OnFocusPlugin/LexicalOnFocusPlugin";
import { PlainTextEditor } from "@/lib/Components/Lexical/PlainTextEditor";
import { EditorCapturePlugin } from "@/lib/Components/Form/Inputs/RichTextInput";
import { useField } from "formik";
import { cn } from "@/lib/utils";
import { BadgeButton } from "@/components/catalyst/badge";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { MarkNode } from "@lexical/mark";

type PlainTextInputProps = {
  name: string;
  label: string;
  className?: string;
  availableReplacements?: string[];
};

class ReplacementMarkNode extends MarkNode {
  static getType() {
    return "replacement-mark";
  }

  static clone(node: ReplacementMarkNode) {
    return new ReplacementMarkNode(node.__ids, node.__key);
  }

  createDOM(_config: any) {
    const dom = document.createElement("span");
    dom.style.backgroundColor = "rgba(59, 130, 246, 0.1)";
    dom.style.color = "rgb(37, 99, 235)";
    dom.style.fontWeight = "500";
    dom.style.padding = "2px 4px";
    dom.style.borderRadius = "4px";
    return dom;
  }

  updateDOM(): boolean {
    return false;
  }

  static importJSON(serializedNode: any) {
    const node = new ReplacementMarkNode(serializedNode.ids);
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: "replacement-mark",
    };
  }
}

function HighlightReplacementsPlugin({
  replacements,
}: {
  replacements: string[];
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!replacements || replacements.length === 0) return;

    const escapeRegex = (str: string) => {
      return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };

    const pattern = new RegExp(
      `(${replacements.map(escapeRegex).join("|")})`,
      "g",
    );

    return editor.registerNodeTransform(TextNode, (textNode) => {
      const text = textNode.getTextContent();
      const parent = textNode.getParent();

      if (parent instanceof ReplacementMarkNode) {
        return;
      }

      pattern.lastIndex = 0;
      const firstMatch = pattern.exec(text);

      if (!firstMatch) return;

      const matchStart = firstMatch.index;
      const matchEnd = matchStart + firstMatch[0].length;

      let targetNode;

      if (matchStart === 0) {
        [targetNode] = textNode.splitText(matchEnd);
      } else {
        [, targetNode] = textNode.splitText(matchStart);
        [targetNode] = targetNode.splitText(firstMatch[0].length);
      }

      const markNode = new ReplacementMarkNode([firstMatch[0]]);
      targetNode.replace(markNode);
      markNode.append(targetNode);
      markNode.selectEnd();
    });
  }, [editor, replacements]);

  return null;
}

function DeleteMarkNodePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handleDelete = (isBackspace: boolean) => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return false;

      const anchor = selection.anchor;
      const focus = selection.focus;
      const anchorNode = anchor.getNode();
      const focusNode = focus.getNode();

      // Check if cursor is inside a ReplacementMarkNode
      const anchorParent = anchorNode.getParent();
      const focusParent = focusNode.getParent();

      // If selection is collapsed (cursor) and inside a mark node
      if (selection.isCollapsed()) {
        if (anchorParent instanceof ReplacementMarkNode) {
          // Delete the entire mark node
          anchorParent.remove();
          return true;
        }

        // Check if backspacing from right after a mark node
        if (isBackspace && anchor.offset === 0) {
          const prevSibling = anchorNode.getPreviousSibling();
          if (prevSibling instanceof ReplacementMarkNode) {
            prevSibling.remove();
            return true;
          }
        }

        // Check if deleting from right before a mark node
        if (
          !isBackspace &&
          anchorNode instanceof TextNode &&
          anchor.offset === anchorNode.getTextContentSize()
        ) {
          const nextSibling = anchorNode.getNextSibling();
          if (nextSibling instanceof ReplacementMarkNode) {
            nextSibling.remove();
            return true;
          }
        }
      } else {
        // Selection spans multiple nodes - if any part is in a mark, remove the whole mark
        if (anchorParent instanceof ReplacementMarkNode) {
          anchorParent.remove();
          return true;
        }
        if (
          focusParent instanceof ReplacementMarkNode &&
          focusParent !== anchorParent
        ) {
          focusParent.remove();
          return true;
        }
      }

      return false;
    };

    const removeBackspace = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      () => handleDelete(true),
      COMMAND_PRIORITY_HIGH,
    );

    const removeDelete = editor.registerCommand(
      KEY_DELETE_COMMAND,
      () => handleDelete(false),
      COMMAND_PRIORITY_HIGH,
    );

    return () => {
      removeBackspace();
      removeDelete();
    };
  }, [editor]);

  return null;
}

export function PlainTextInput({
  name,
  className,
  label,
  availableReplacements = [],
}: PlainTextInputProps) {
  const [hasTyped, setHasTyped] = useState(false);
  const [field, meta, helpers] = useField(name);
  const showError = meta.touched && !!meta.error;
  const { setValue, setTouched } = helpers;
  const editorRef = useRef<LexicalEditor | null>(null);

  function handleReplacementClick(replacement: string) {
    if (!editorRef.current) return;

    editorRef.current.update(() => {
      const selection = $getSelection();
      const textNode = $createTextNode(replacement);

      if ($isRangeSelection(selection)) {
        selection.insertNodes([textNode]);
      } else {
        const root = $getRoot();
        const firstChild = root.getFirstChild();

        if (firstChild) {
          firstChild.selectEnd();
          const newSelection = $getSelection();
          if ($isRangeSelection(newSelection)) {
            newSelection.insertNodes([textNode]);
          }
        }
      }

      editorRef.current?.focus();
    });
  }

  return (
    <GenericField
      name={name}
      label={label}
      viewNode={field.value}
      className={className}
    >
      {() => (
        <div className="relative">
          <PlainTextEditor
            initialHtml={field.value}
            name={name}
            nodes={[ReplacementMarkNode]}
            className={cn(
              "shadow-xs focus:outline-hidden focus-visible:outline-hidden block w-full rounded-md border border-gray-300 px-3 py-2 text-sm transition-shadow duration-100 focus:ring-1",
              showError
                ? "border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500"
                : "focus:border-indigo-500 focus:ring-indigo-500",
            )}
          >
            <OnChangePlugin
              ignoreHistoryMergeTagChange={false}
              onChange={(editorState, editor) => {
                editor.update(() => {
                  const root = $getRoot();

                  if (root.getTextContentSize() > 0) {
                    setValue(root.getTextContent());

                    setHasTyped(true);
                  } else {
                    setValue(null);
                  }
                });
              }}
            />
            <LexicalOnFocusPlugin
              onBlur={() => {
                if (hasTyped) {
                  setTouched(true);
                }
              }}
            />
            <HighlightReplacementsPlugin replacements={availableReplacements} />
            <DeleteMarkNodePlugin />
            <EditorCapturePlugin ref={editorRef} />
          </PlainTextEditor>
          {availableReplacements.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-1">
              {availableReplacements.map((replacement) => (
                <BadgeButton
                  key={replacement}
                  color="blue"
                  onClick={() => handleReplacementClick(replacement)}
                >
                  {replacement}
                </BadgeButton>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </GenericField>
  );
}
